import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { VerificationStatusEnum } from 'src/app/pages/identity-verification/enums/verification-status.enum';
import { IdentityVerificationApiInterfaceService } from 'src/app/pages/identity-verification/services/identity-verification-api-interface.service';
import { VerificationStatus } from 'src/app/pages/identity-verification/types/identity-verification.types';
import { IdentityVerificationModalComponent } from '../components/identity-verification-modal/identity-verification-modal.component';
import { AccountService } from './account.service';
import { IdVerificationEligibilityService } from './id-verification-eligibility.service';
import { ModalDialogService } from './modal-dialog.service';

@Injectable({
  providedIn: 'root',
})
export class IdVerificationModalService {
  constructor(
    private accountService: AccountService,
    private identityVerificationService: IdentityVerificationApiInterfaceService,
    private modalDialogService: ModalDialogService,
    private idVerificationEligibilityService: IdVerificationEligibilityService,
  ) {}

  /**
   * Checks verification status and shows modal if needed
   *
   * @returns Observable<boolean> - returns true if verified or can proceed, false if blocked
   */
  showVerificationModalIfNeeded$(): Observable<boolean> {
    return this.checkVerificationStatus$().pipe(
      switchMap((verificationResult) => {
        if (!verificationResult.isVerified) {
          return this.createIdVerificationModal$(verificationResult.status).pipe(
            map(() => false),
            catchError((error) => {
              console.error('Error presenting verification modal:', error);
              return of(false);
            }),
          );
        }
        return of(true);
      }),
    );
  }

  /**
   * Checks if user verification is required and verified
   * When the user is eligible and mandatory, we check the verification status,
   * otherwise we return true, so the user can proceed to showVerificationModalIfNeeded
   *
   * @returns Observable<{ isVerified: boolean; status: VerificationStatusEnum }> - The verification status of the user
   */
  private checkVerificationStatus$(): Observable<{ isVerified: boolean; status: VerificationStatusEnum }> {
    return this.idVerificationEligibilityService.checkEligibility().pipe(
      map((eligibilityStatus) => {
        const isEligibleAndMandatory = eligibilityStatus.eligible && eligibilityStatus.mandatory;
        return isEligibleAndMandatory;
      }),
      switchMap((isEligibleAndMandatory) =>
        isEligibleAndMandatory ? this.getUserIdVerificationStatus$() : of({ isVerified: true, status: null }),
      ),
      map((result) => {
        if ('verification_status' in result) {
          return {
            isVerified: result.verification_status === VerificationStatusEnum.VERIFIED,
            status: result.verification_status,
          };
        }
        return result;
      }),
    );
  }

  /**
   * Fetches and sets the user identity verification status.
   *
   * @returns Observable<VerificationStatus> - The verification status of the user
   */
  private getUserIdVerificationStatus$(): Observable<VerificationStatus> {
    const userUuid: string = this.accountService.uuid;
    return this.identityVerificationService.getVerificationStatus(userUuid);
  }

  /**
   * Creates ID verification modal with appropriate status
   *
   * @param verificationStatus - The verification status to pass to the modal
   */
  private createIdVerificationModal$(verificationStatus: VerificationStatusEnum): Observable<void> {
    return this.modalDialogService.addDialogToQueue({
      component: IdentityVerificationModalComponent,
      cssClass: 'identity-verification-modal',
      backdropDismiss: true,
      componentProps: {
        verificationStatus,
      },
    });
  }
}
